/**
 * Vietnamese-aware text normalisation and fuzzy ranking for address search.
 */

const COMBINING = /[\u0300-\u036f]/g;

/** Lower-case, strip diacritics (đ -> d), keep letters/digits and / - as token chars. */
export function fold(s) {
  return String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(COMBINING, '')
    .replace(/đ/g, 'd')
    .replace(/[^\p{L}\p{N}/\-\s]/gu, ' ')
    .replace(/(^|\s)-+|-+(?=\s|$)/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Lower-case keeping diacritics (NFC) — for exact-accent bonus. */
export function lowerNFC(s) {
  return String(s || '').toLowerCase().normalize('NFC').replace(/[^\p{L}\p{N}/\-\s]/gu, ' ').replace(/\s+/g, ' ').trim();
}

export const tokens = (s) => fold(s).split(' ').filter(Boolean);

/** Generic words that carry little identifying information. */
const WEAK = new Set(['ha', 'noi', 'hanoi', 'viet', 'nam', 'vietnam', 'vnm', 'thanh', 'pho', 'tp', 'so', 'duong', 'phuong', 'quan', 'huyen', 'xa', 'thi', 'tran', 'khu', 'to', 'cum', 'thon', 'ngo', 'ngach', 'hem', 'kiet', 'p', 'q']);
const MEDIUM = new Set(['ngo', 'ngach', 'pho', 'duong']);

/**
 * Clean a free-text query before sending it to geocoders:
 * expands common Vietnamese abbreviations and removes noise.
 */
export function cleanQuery(raw) {
  let s = ' ' + String(raw || '').normalize('NFC').replace(/[\u200b-\u200d\ufeff]/g, '') + ' ';
  const rules = [
    [/([\s,(])(?:tp|t\.p)\.?\s+(?=h[àa]\s*n[ộo]i)/giu, '$1'],
    [/([\s,(])hn(?=[\s,).]|$)/giu, '$1Hà Nội'],
    [/([\s,(])k[đd]t(?=[\s,.])/giu, '$1Khu đô thị'],
    [/([\s,(])kcn(?=[\s,.])/giu, '$1Khu công nghiệp'],
    [/([\s,(])bv(?=[\s,.])/giu, '$1Bệnh viện'],
    [/([\s,(])[đd]h(?=[\s,.])/giu, '$1Đại học'],
    [/([\s,(])ngh\.\s*/giu, '$1Ngách '],
    [/([\s,(])ng\.\s*/giu, '$1Ngõ '],
    [/([\s,(])[đd]\.\s*/giu, '$1Đường '],
    [/([\s,(])q\.\s*/giu, '$1Quận '],
    [/([\s,(])tx\.\s*/giu, '$1Thị xã '],
    [/([\s,(])tt\.\s*/giu, '$1Thị trấn '],
    [/([\s,(])h\.\s*/giu, '$1Huyện '],
    [/([\s,(])x\.\s*/giu, '$1Xã '],
    [/([\s,(])p\.\s*/giu, '$1'], // P. = Phố or Phường (ambiguous) -> drop the marker
    [/([\s,(])s[ốo]\s+(?=\d)/giu, '$1'], // "số 12" -> "12"
    [/\s*,\s*/g, ', '],
    [/\s+/g, ' '],
  ];
  for (const [re, rep] of rules) s = s.replace(re, rep);
  return s.replace(/^[\s,]+|[\s,]+$/g, '');
}

/** Damerau–Levenshtein distance with early exit above `max`. */
export function editDistance(a, b, max = 2) {
  if (a === b) return 0;
  const la = a.length, lb = b.length;
  if (Math.abs(la - lb) > max) return max + 1;
  let prev2 = null, prev = new Array(lb + 1), cur = new Array(lb + 1);
  for (let j = 0; j <= lb; j++) prev[j] = j;
  for (let i = 1; i <= la; i++) {
    cur[0] = i;
    let rowMin = cur[0];
    for (let j = 1; j <= lb; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      if (prev2 && i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) v = Math.min(v, prev2[j - 2] + 1);
      cur[j] = v;
      if (v < rowMin) rowMin = v;
    }
    if (rowMin > max) return max + 1;
    prev2 = prev; prev = cur; cur = new Array(lb + 1);
  }
  return prev[lb];
}

const isNum = (t) => /^\d/.test(t);

function tokenScore(q, c, isLast) {
  if (q === c) return 1;
  if (isNum(q) || isNum(c)) {
    // house numbers: 12 vs 12a / 12/3 -> partial; otherwise no match
    if (isNum(q) && isNum(c)) {
      const qb = q.match(/^\d+/)[0], cb = c.match(/^\d+/)[0];
      if (qb === cb) return 0.72;
      if (isLast && c.startsWith(q)) return 0.6;
    }
    return 0;
  }
  if (c.startsWith(q)) return isLast ? 0.92 + 0.08 * (q.length / c.length) : 0.75 * (q.length / c.length) + 0.1;
  if (q.length >= 4) {
    const d = editDistance(q, c, 2);
    if (d === 1) return 0.74;
    if (d === 2 && q.length >= 7) return 0.5;
  }
  return 0;
}

/**
 * Similarity between query tokens and candidate tokens in [0, 1].
 * Weighted by token informativeness; rewards in-order matches.
 */
export function similarity(qTokens, cTokens) {
  if (!qTokens.length || !cTokens.length) return 0;
  let total = 0, got = 0, lastPos = -1, ordered = 0;
  const used = new Uint8Array(cTokens.length);
  qTokens.forEach((q, qi) => {
    const w = isNum(q) ? 1.6 : WEAK.has(q) ? (MEDIUM.has(q) ? 0.55 : 0.3) : 1;
    total += w;
    let best = 0, bestJ = -1;
    for (let j = 0; j < cTokens.length; j++) {
      if (used[j]) continue;
      const s = tokenScore(q, cTokens[j], qi === qTokens.length - 1);
      if (s > best || (s === best && s > 0 && Math.abs(j - lastPos - 1) < Math.abs(bestJ - lastPos - 1))) { best = s; bestJ = j; }
      if (best === 1 && j > lastPos) break;
    }
    if (bestJ >= 0 && best > 0) {
      used[bestJ] = 1;
      got += w * best;
      if (bestJ > lastPos) ordered++;
      lastPos = bestJ;
    }
  });
  const base = got / total;
  const orderBonus = qTokens.length > 1 ? 0.06 * (ordered / qTokens.length) : 0.06;
  // penalise very long candidates slightly (prefer concise exact names)
  const extra = Math.max(0, cTokens.length - qTokens.length);
  const lenPenalty = Math.min(0.08, extra * 0.006);
  return Math.max(0, Math.min(1, base * 0.94 + orderBonus - lenPenalty));
}

/** Weighted share of candidate-name tokens matched by some query token, in [0, 1]. */
export function labelPrecision(qTokens, cTokens) {
  if (!qTokens.length || !cTokens.length) return 0;
  let total = 0, got = 0;
  for (const c of cTokens) {
    const w = isNum(c) ? 1.2 : WEAK.has(c) ? 0.35 : 1;
    total += w;
    let best = 0;
    for (const q of qTokens) {
      const t = tokenScore(q, c, false) >= 0.7 ? 1 : q.length >= 2 && c.startsWith(q) ? 0.6 : 0;
      if (t > best) best = t;
    }
    got += w * best;
  }
  return got / total;
}

/** Extract a leading house number (e.g. "12", "12A", "12/3", "số 5") from a query. */
export function houseNumberOf(s) {
  const m = fold(s).match(/^(?:so\s+)?(\d+[a-z]?(?:\/\d+[a-z]?)*)\b/);
  return m ? m[1] : null;
}

/**
 * Rank and de-duplicate geocoder candidates.
 * candidate: { label, sub, source: 'esri'|'photon'|'local', rank, lat?, lng?, kind? }
 * ctx: { center: {lat, lng}, query }
 */
export function rankCandidates(query, candidates, ctx = {}) {
  const qt = tokens(query);
  const qn = houseNumberOf(query);
  const qAccent = lowerNFC(query).split(' ');
  const hasAccent = /[^\x00-\x7f]/.test(query);
  const center = ctx.center;
  const scored = candidates.map((c) => {
    const main = tokens(c.label);
    const all = main.concat(tokens(c.sub || ''));
    const simMain = similarity(qt, main);
    const simAll = similarity(qt, all);
    let s = Math.max(simMain * 1.02, simAll);
    // precision: how much of the *name* the query explains ("Hồ Gươm" beats "Ho Guom Hotel"
    // for "ho guom"; a café at "1 Tràng Tiền" loses to the address itself)
    s += 0.07 * labelPrecision(qt, main);
    if (main.length && main.join(' ') === qt.join(' ')) s += 0.03;
    // accent-exact bonus (e.g. "Hàng Bạc" vs "Hàng Bác")
    if (hasAccent) {
      const ca = new Set(lowerNFC(c.label + ' ' + (c.sub || '')).split(' '));
      const hit = qAccent.filter((t) => t.length > 1 && ca.has(t)).length;
      s += 0.05 * (hit / Math.max(1, qAccent.length));
    }
    // house-number agreement
    const cn = houseNumberOf(c.label);
    if (qn) {
      if (cn === qn) s += 0.12;
      else if (cn && cn.replace(/\D.*$/, '') === qn.replace(/\D.*$/, '')) s += 0.05;
      else if (cn) s -= 0.1;
      else s -= 0.04;
    } else if (cn) s -= 0.02;
    // provider prior (their own ranking is informative)
    const prior = Math.max(0, 1 - (c.rank || 0) * 0.13);
    s += (c.source === 'local' ? 0.06 : c.source === 'esri' ? 0.05 : 0.04) * prior;
    if (c.boost) s += c.boost;
    // proximity to current view (weak)
    if (center && Number.isFinite(c.lat)) {
      const dKm = Math.hypot((c.lat - center.lat) * 110.6, (c.lng - center.lng) * 103.9);
      s += 0.035 * Math.exp(-dKm / 6);
    }
    return { ...c, score: s };
  });
  // de-duplicate: same folded label, or near-identical location & similar text
  scored.sort((a, b) => b.score - a.score);
  const out = [];
  for (const c of scored) {
    const fl = fold(c.label);
    const dup = out.find((o) => {
      const ol = fold(o.label);
      if (ol === fl && (!Number.isFinite(o.lat) || !Number.isFinite(c.lat) || Math.hypot((o.lat - c.lat) * 110600, (o.lng - c.lng) * 103900) < 250)) return true;
      if (Number.isFinite(o.lat) && Number.isFinite(c.lat)) {
        const d = Math.hypot((o.lat - c.lat) * 110600, (o.lng - c.lng) * 103900);
        if (d < 40 && similarity(tokens(o.label), tokens(c.label)) > 0.8) return true;
      }
      return false;
    });
    if (dup) {
      dup.sources = Array.from(new Set([...(dup.sources || [dup.source]), c.source]));
      if (!Number.isFinite(dup.lat) && Number.isFinite(c.lat)) { dup.lat = c.lat; dup.lng = c.lng; }
      dup.score += 0.03; // agreement between providers
      continue;
    }
    out.push({ ...c, sources: [c.source] });
  }
  return out.sort((a, b) => b.score - a.score);
}

/** Wrap matched query tokens in <mark> for display (accent-insensitive). */
export function highlight(label, query, escapeHtml) {
  const qt = tokens(query).filter((t) => t.length > 0);
  if (!qt.length) return escapeHtml(label);
  const parts = String(label).split(/(\s+|,)/);
  return parts.map((p) => {
    const f = fold(p);
    if (!f) return escapeHtml(p);
    const hit = qt.some((q) => f === q || (f.startsWith(q) && q.length >= 1) || (q.length >= 4 && editDistance(q, f, 1) <= 1));
    return hit ? `<mark>${escapeHtml(p)}</mark>` : escapeHtml(p);
  }).join('');
}
